import type {
  ArticleCategory,
  ColorVariant,
  ContactFormSubmitType,
  ExternalLinkItem,
  IImage,
  IImages,
  LinkTarget,
  MediaType,
  PageData,
  TAccordion,
  TButtonLink,
  TContentCard,
  TCtaCard,
  TFormField,
  TProduct,
  TTestimonyCard,
  TTitleDescriptionImage,
} from "./common";

export type TitleNumber = {
  id: number;
  title: string;
  number: string;
};

export type TitleTextIconImage = {
  id: number;
  title: string;
  text?: string;
  icon?: IImage;
  image?: IImage;
};

export type TextBlock = {
  id: number;
  text?: string;
};

export type TTitleSubtitle = {
  title: string;
  subTitle: string;
};

export type TIconTitleDescription = {
  icon?: IImage;
  title: string;
  description?: string;
};

export type TitleWithTwoColumnsBlock = {
  title: string;
  leftColumn: TextBlock[];
  rightColumn: TextBlock[];
};

export type TextImageBlock = {
  id: number;
  text?: string;
  imageMobile?: IImage;
  imageDesktop?: IImage;
  buttonLabel?: string;
  buttonHref?: string;
};

export type TitleSubtitleButtonText = {
  title: string;
  subTitle?: string;
  buttonText: string;
};

export type TitleSubtitleImageButtonText = {
  title: string;
  subTitle?: string;
  buttonText: string;
  buttonLink?: string;
  image?: IImage;
};

export type TextSubtextImageBlock = {
  id: number;
  text?: string;
  subtext?: string;
  image?: IImage;
};

export type TextTextareaBlock = {
  id: number;
  title?: string;
  textarea?: string;
};

export type TIconTitleTextBlock = {
  id: number;
  title: string;
  icon?: IImage;
  text?: string;
  description?: string;
};

export type TIconTitleBlock = {
  id: number;
  icon: IImage;
  title: string;
};

export type CarouselBlock = {
  id: number;
  list: TextImageBlock[];
  secondsToChange?: number;
};

export type TRetailBenefitsRequirementsBlock = {
  title: string;
  imageDesktop?: IImage;
  imageMobile?: IImage;
  list: TIconTitleBlock[];
};

export enum SectionComponent {
  Securement = "sections.securement",
  SecurementAndPartnersSection = "sections.securement-and-partners-section",
  Title = "sections.title-section",
  RetailBenefitsRequirementsSection = "retail.benefits-requirements-section",
  RetailInterestRatesSection = "retail.interest-rates-section",
  RetailDetailedInformationSection = "retail.detailed-information-section",
  Map = "contacts.map-section",
  BenefitsSection = "sections.benefits-section",
  CarouselStepsSection = "sections.carousel-steps-section",
  AboutUsSection = "sections.about-us-section",
  MissionSection = "sections.mission-section",
  OurValuesSection = "sections.our-values-section",
  OurTeamSection = "sections.our-team-section",
  OurHistorySection = "sections.our-history-section",
  OurStrategicPartnersSection = "sections.our-stragetic-partners-section",
  PhotoFeedbackSection = "sections.photo-feedback-section",
  MainBannersSection = "sections.main-banners-section",
  ProductSwitcherSection = "retail.product-switcher-section",
  PromotionInformationSection = "retail.promotion-information-section",
  BankPromoSection = "sections.bank-promo-section",
  PromoOfBankSection = "sections.promo-of-bank-section",
  DownloadFilesSection = "sections.download-files-section",
  MapImageSection = "sections.map-image-section",
  CtaSection = "sections.cta-section",
  ProductHighlightSection = "sections.product-highlight-section",
  GridInfoSection = "sections.grid-info-section",
  FaqSection = "sections.faq-section",
  ContactSection = "sections.contact-section",
  LocationSection = "sections.location-section",
  ComplianceInfoSection = "sections.compliance-info-section",
  TestimonialSection = "sections.testimonial-section",
  InformationSection = "sections.information-section",
  ProductInformationSection = "sections.product-information-section",

  ProductCtaSection = "sections.product-cta-section",
  ProductAboutSection = "sections.product-about-section",
  ProductHowToSection = "sections.product-how-to-section",
  ProductRequirementSection = "sections.product-requirement-section",
  ProductBenefitSection = "sections.product-benefit-section",
  ProductComparisonSection = "sections.product-comparison-section",
  ProductCalculatorSection = "sections.product-calculator-section",
  ProductContactSection = "sections.product-contact-section",

  CareerInfoGridSection = "sections.career-info-grid-section",
  CareerSection = "sections.career-section",
  CareerPointSection = "sections.career-point-section",
  CareerDescriptionSection = "sections.career-description-section",

  CarEquityCtaSection = "sections.car-equity-cta-section",

  ArticleGridSection = "sections.article-grid-section",
}

export type CarouselStepsSectionType = {
  id: number;
  title: string;
  button?: ExternalLinkItem;
  steps: {
    id: number;
    text?: string;
    imageMobile: IImage;
    imageDesktop: IImage;
    content?: string;
  }[];
  __component: SectionComponent.CarouselStepsSection;
};

export type SecurementSection = {
  id: number;
  title: string;
  description?: string;
  image?: IImage;
  __component: SectionComponent.Securement;
};

export type SecurementAndPartnersSectionType = {
  id: number;
  title: string;
  subTitle?: string;
  partners?: IImages;
  __component: SectionComponent.SecurementAndPartnersSection;
};

export type BaseColumn = { id: number; list: TextTextareaBlock[] };

export type TRetailDetailedInformationSection = {
  id: number;
  list: { id: number; title: string; defaultState?: "open" | "closed"; list: TitleWithTwoColumnsBlock[] }[];
  __component: SectionComponent.RetailDetailedInformationSection;
};

export type TRetailBenefitsRequirementsSection = {
  id: number;
  benefits: TRetailBenefitsRequirementsBlock;
  requirements: TRetailBenefitsRequirementsBlock;
  __component: SectionComponent.RetailBenefitsRequirementsSection;
};

export type TRetailInterestRateBlock = {
  id: number;
  month: number;
  monthLabel?: string;
  buttonText: string;
  image: IImage;
};

export type TRetailInterestRatesSection = {
  id: number;
  title: string;
  subTitle?: string;
  rates: TRetailInterestRateBlock[];
  learnMore: TitleSubtitleImageButtonText;
  hidden: boolean;
  __component: SectionComponent.RetailInterestRatesSection;
};

export type TTitleSection = {
  id: number;
  title: string;
  subTitle?: string;
  imageDesktop?: IImage;
  imageMobile?: IImage;
  button?: { text: string; link: string };
  background?: string;
  __component: SectionComponent.Title;
};

export type TBenefitsSection = {
  id: number;
  title: string;
  subTitle?: string;
  benefits: TitleTextIconImage[];
  __component: SectionComponent.BenefitsSection;
};

export type TMapSection = {
  id: number;
  mapSrc: string;
  __component: SectionComponent.Map;
};

export type TAboutUsSection = {
  id: number;
  title: string;
  description: string;
  banner: IImage;
  visionTitle: string;
  visionContent?: string;
  missionTitle: string;
  missions: TIconTitleDescription[];
  __component: SectionComponent.AboutUsSection;
};

export type TMissionSection = {
  id: number;
  title: string;
  list: TIconTitleDescription[];
  __component: SectionComponent.MissionSection;
};

export type TOurValuesSection = {
  id: number;
  title: string;
  description: string;
  list: TIconTitleDescription[];
  ornament?: IImage;
  __component: SectionComponent.OurValuesSection;
};

export type TOurTeamPerson = {
  avatar: IImage;
  name: string;
  position: string;
  description: string;
};

export type TOurTeamSection = {
  id: number;
  title: string;
  description: string;
  team: {
    title: string;
    persons: TOurTeamPerson[];
  }[];
  __component: SectionComponent.OurTeamSection;
};

export type TOurHistorySectionDate = { id: string | number; date: number; description: string; title: string; icon?: IImage };
export type TOurHistorySection = {
  id: number;
  title: string;
  dates: TOurHistorySectionDate[];
  secondsToChange: number;
  ornament?: IImage;
  __component: SectionComponent.OurHistorySection;
};

export type TOurStrategicPartnersSection = {
  id: number;
  title: string;
  description: string;
  logo?: IImage;
  partners: { title: string; logos: IImages }[];
  __component: SectionComponent.OurStrategicPartnersSection;
};

export type TDownloadFilesItem = {
  fileName: string;
  icon: IImage;
  buttonText: string;
  file: MediaType;
};

export type TDownloadFilesSection = {
  id: number;
  title: string;
  description: string;
  itemsTitle?: string;
  items: TDownloadFilesItem[];
  __component: SectionComponent.DownloadFilesSection;
};

export type TPhotoFeedbackSection = {
  title?: string;
  list: IImages;
  __component: SectionComponent.PhotoFeedbackSection;
};

export type TMainBannersSection = {
  secondsToChange: number;
  banners: {
    link?: string;
    linkText?: string;
    imageMobile: IImage;
    imageDesktop: IImage;
    background?: IImage;
    backgroundMobile?: IImage;
    logo?: IImage;
    linkVariant: ColorVariant;
    title: string;
    titleVariant: ColorVariant;
    footnote?: string;
    phone?: string;
    subtitle?: string;
    description?: string;
    logoText?: string;
    linkIcon?: IImage;
  }[];
  footnote?: string;
  tabs?: TButtonLink[];
  __component: SectionComponent.MainBannersSection;
};

export type TProductSwitcherSection = {
  products: {
    data: PageData<{
      title: string;
      background: "light" | "dark";
      imageMobile: IImage;
      imageDesktop: IImage;
    }>[];
  };
  __component: SectionComponent.ProductSwitcherSection;
};

export type TPromotionInformationSection = {
  list: {
    title: string;
    description: string;
  }[];
  buttons?: ExternalLinkItem[];
  __component: SectionComponent.PromotionInformationSection;
};

export type TBankPromoSection = {
  imageMobile: IImage;
  imageDesktop: IImage;
  button: ExternalLinkItem;
  main: TTitleDescriptionImage;
  list: TTitleDescriptionImage[];
  __component: SectionComponent.BankPromoSection | SectionComponent.PromoOfBankSection;
};

export type TMapImageSection = {
  title?: string;
  mapSrc: string;
  imageMobile: IImage;
  imageDesktop: IImage;
  __component: SectionComponent.MapImageSection;
};

export type TCtaSection = {
  id: string | number;
  title: string;
  description: string;
  button?: TButtonLink;
  background?: IImage;
  illustration: IImage;
  __component: SectionComponent.CtaSection;
};

export type TProductHighlightSection = {
  id: string | number;
  title: string;
  description: string;
  products: TContentCard[];
  ornament?: IImage;
  __component: SectionComponent.ProductHighlightSection;
};

export type TGridInfoSection = {
  id: string | number;
  title: string;
  description: string;
  items: TContentCard[];
  cta?: TCtaCard;
  cols?: number;
  __component: SectionComponent.GridInfoSection;
};

export type TFaqSection = {
  id: string | number;
  title: string;
  description: string;
  accordions: { data: TAccordion[] };
  __component: SectionComponent.FaqSection;
};

export type TContactSection = {
  id: number;
  title: string;
  subtitle?: string;
  description: string;
  contacts: TIconTitleTextBlock[];
  formFields: TFormField[];
  buttonText: string;
  submitType: ContactFormSubmitType;
  agreementText?: string;
  __component: SectionComponent.ContactSection;
};

export type TLocationSection = {
  id: number;
  title: string;
  mapButtonLink: TButtonLink;
  embedMapLink?: string;
  images: IImages;
  address: string;
  __component: SectionComponent.LocationSection;
};

export type TComplianceInfoSection = {
  id: number;
  title: string;
  description: string;
  icon?: IImage;
  ornament?: IImage;
  __component: SectionComponent.ComplianceInfoSection;
};

export type TTestimonialSection = {
  id: number;
  title: string;
  description: string;
  testimonies: TTestimonyCard[];
  secondsToChange: number;
  __component: SectionComponent.TestimonialSection;
};

export type TInformationSection = {
  title: string;
  description?: string;
  imageMobile?: IImage;
  imageDesktop?: IImage;
  button?: TButtonLink;
  __component: SectionComponent.InformationSection;
};

// Product Cta Section Types
export type TProductCtaSection = {
  illustration: IImage;
  menuButtons?: TButtonLink[];
  subTitle?: string;
  title: string;
  description: string;
  buttonLink: TButtonLink;
  __component: SectionComponent.ProductCtaSection;
};

// Product About Section Types
export type TProductAboutSection = {
  image?: IImage;
  title: string;
  description: string;
  items: TContentCard[];
  __component: SectionComponent.ProductAboutSection;
};

// Product How To Section Types
export type TProductHowToSection = {
  title: string;
  description: string;
  items: TProductHowToItem[];
  background?: IImage;
  __component: SectionComponent.ProductHowToSection;
};

export type TProductHowToItem = {
  id: string | number;
  title: string;
  description: string;
  color: "primary" | "green";
};

export type TProductRequirementItem = {
  id: string;
  title: string;
  content: string;
};

export type TProductRequirementSection = {
  title: string;
  items: TProductRequirementItem[];
  __component: SectionComponent.ProductRequirementSection;
};

export type TProductBenefitSection = {
  title: string;
  items: TContentCard[];
  requirementTitle: string;
  requirementItems: TProductRequirementItem[];
  __component: SectionComponent.ProductBenefitSection;
};

export type TProductComparisonSection = {
  title: string;
  description: string;
  cards: TProductComparisonCard[];
  __component: SectionComponent.ProductComparisonSection;
};

export type TProductComparisonCard = {
  title: string;
  description: string;
  subtitle: string;
  highlighted: boolean;
  icon: IImage;
  features: string[];
  buttonText: string;
  buttonLink: string;
  buttonIcon?: IImage;
  buttonTarget: LinkTarget;
};

export type TProductContactSection = {
  title: string;
  description: string;
  formFields: TFormField[];
  buttonText: string;
  footnote?: string;
  submitType: ContactFormSubmitType;
  background: IImage;
  formDefaultValue?: any;
  __component: SectionComponent.ProductContactSection;
};

export type TProductCalculatorSection = {
  title: string;
  description: string;
  notes?: string[];
  button?: TButtonLink;
  product: {
    data: {
      id: number;
      attributes: TProduct;
    };
  };
  __component: SectionComponent.ProductCalculatorSection;
};

export type TCareerItem = {
  id: string | number;
  title: string;
  slug: string;
  location: string;
  type: string;
  image?: IImage;
  internalLink: TButtonLink;
  externalLink: TButtonLink;
  useLink: "internalLink" | "externalLink";
  buttons?: TButtonLink[];
  sharedLink?: string;
  publishedDate?: string;
  isRecommendedSearch?: boolean;
};

export type TCareerInfoGridSection = {
  title: string;
  description: string;
  image?: IImage;
  itemTitle: string;
  items: TContentCard[];
  colSize: number;
  __component: SectionComponent.CareerInfoGridSection;
};

export type TCareerDescriptionSection = {
  title: string;
  description: string;
  __component: SectionComponent.CareerDescriptionSection;
};

export type TCareerPointSection = {
  title: string;
  points: {
    icon?: IImage;
    text: string;
  }[];
  showIcon: boolean;
  __component: SectionComponent.CareerPointSection;
};

export type CareersCollection = {
  data: {
    id: number;
    attributes: TCareerItem;
  }[];
};

export type TCareerSection = {
  title: string;
  description: string;
  items: {
    title: string;
    careers: CareersCollection;
  }[];
  __component: SectionComponent.CareerSection;
};

export type TNotFoundSection = {
  title: string;
  decorativeTitle: string;
  description: string;
  buttons?: TButtonLink[];
  image: IImage;
};

export type TCarEquityCtaSection = {
  id: string | number;
  title: string;
  highlightedTitle?: string;
  highlightedTitleColor?: string;
  description: string;
  button?: TButtonLink;
  image: IImage;
  items?: TContentCard[];
  __component: SectionComponent.CarEquityCtaSection;
};

export type TArticle = {
  categories: ArticleCategory[];
  content: string;
  fullTitle: string;
  image: IImage;
  isHighlighted: boolean;
  isRecommendedSearch: boolean;
  publishedDate: Date;
  readMoreButtonText: string;
  relatedArticles: { data: PageData<TArticle>[] };
  shortTitle: string;
  slug: string;
  tags: any;
};

export type TArticleGridSection = {
  title: string;
  category: ArticleCategory;
  articles: { data: PageData<TArticle>[] };
  __component: SectionComponent.ArticleGridSection;
};
